'use client';

import { type KeyboardEvent, useCallback, useRef, useState } from 'react';

import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import clsx from 'clsx';
import { Plus, SendHorizontal } from 'lucide-react';

import type { OrderMode } from '../../../orders/types';
import { useChatContext } from '../../hooks/useChatContext';
import { useChatMessageAPI } from '../../hooks/useChatMessageAPI';
import { useChatSendCapability } from '../../hooks/useChatSendCapability';
import { ChatMessageType } from '../../types/ChatMessage';
import { ShareMenu } from './ShareMenu';
import { ShareOrderDialog } from './order-picker/ShareOrderDialog';
import { useShareMedia } from './useShareMedia';
import { useShareOrder } from './useShareOrder';

interface Props {
  /**
   * Which side of the conversation the sender is on. Drives "Share
   * Order": a `seller` (creator) shares orders sold to this buyer, a
   * `buyer` (fan) shares their purchases from this creator.
   */
  senderRole: OrderMode;
  /**
   * Visitor host handler — on a gated send (`subscription-required`), route to
   * the subscribe flow instead of calling the send API. Omitted on surfaces
   * where the sender is always the creator (they never hit this state).
   */
  onSubscribeRequired?: (minLevel?: PopStoreExtendedSubscriptionLevel) => void;
  /**
   * Extra bottom space, in px, so the composer clears the mobile bottom nav —
   * this bar is the room's bottom-most element whenever it renders. Measured by
   * `ChatRoom`; `undefined` wherever no bar is on screen (desktop, hosts outside
   * the dashboard layout), leaving the class's own `py-3`.
   */
  bottomInset?: number;
}

/**
 * Message composer. Enter sends; Shift+Enter inserts a newline. A `+`
 * menu shares a photo, a video, or an order (1:1 with upstream's
 * `ChatActionBar`). The send button stays disabled while a text send is
 * in flight so rapid double-taps don't fire duplicate requests.
 *
 * NOT `useTransition`: `sendMessage` emits the optimistic `sending-message`
 * event synchronously (before its network `await`), which a provider folds into
 * the message list. React 19 batches transition-priority updates to the END of
 * an async transition, so a transition would suppress that optimistic bubble
 * until the send resolved. A plain `useState` pending flag + async IIFE keeps the
 * optimistic insert at urgent priority so it paints immediately.
 */
export function ChatInputBar({
  senderRole,
  onSubscribeRequired,
  bottomInset,
}: Props) {
  const { sessionType, currentUser, targetUser, quotedMessage$ } =
    useChatContext();
  const { sendMessage } = useChatMessageAPI();
  const capability = useChatSendCapability();
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { sharePhoto, shareVideo } = useShareMedia({
    sendMessage,
    sessionType,
    currentUser,
    targetUser,
  });
  const { isPickerOpen, openPicker, closePicker, shareOrders } = useShareOrder({
    sendMessage,
    sessionType,
    currentUser,
    targetUser,
    role: senderRole,
  });

  const gateBlocked =
    capability.status === 'loading' || capability.status === 'disabled';
  const subscriptionRequired = capability.status === 'subscription-required';

  const submit = useCallback(() => {
    const text = draft.trim();
    if (!text || pending) return;
    // Send gating (1:1 with upstream `ChatActionBar`): can't send while the
    // capability is resolving or disabled; a `subscription-required` send routes
    // to the subscribe flow instead of hitting the API.
    if (capability.status === 'loading' || capability.status === 'disabled') {
      return;
    }
    if (capability.status === 'subscription-required') {
      onSubscribeRequired?.(capability.minLevel);
      return;
    }

    setDraft('');
    const quoted = quotedMessage$.getValue();
    quotedMessage$.next(undefined);

    setPending(true);
    void (async () => {
      try {
        await sendMessage({
          sessionType,
          type: ChatMessageType.Text,
          from: currentUser,
          to: targetUser,
          payload: { text },
          quoted,
        });
      } catch (e) {
        // Surface the message back to the input so the user can retry
        // without retyping. The failure is also reflected on the
        // optimistic bubble via the `send-message-failed` event.
        console.error('send message failed', e);
        setDraft(text);
      } finally {
        setPending(false);
      }
    })();
  }, [
    capability,
    currentUser,
    draft,
    onSubscribeRequired,
    pending,
    quotedMessage$,
    sendMessage,
    sessionType,
    targetUser,
  ]);

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      submit();
    }
  };

  // Send is actionable with text + not pending, unless the gate is still
  // resolving or hard-disabled. `subscription-required` stays actionable — the
  // tap routes to the subscribe flow.
  const canSend = draft.trim().length > 0 && !pending && !gateBlocked;
  const placeholder =
    capability.status === 'loading'
      ? 'Loading…'
      : capability.status === 'subscription-required'
        ? 'Subscribe to say hi'
        : capability.status === 'disabled'
          ? 'Messaging unavailable'
          : 'Type a message…';

  return (
    <form
      className="border-line-strong/80 [[data-theme=visitor]_&]:border-line bg-surface-2 flex items-end gap-2 border-t px-4 py-3"
      style={bottomInset == null ? undefined : { paddingBottom: bottomInset }}
      onSubmit={event => {
        event.preventDefault();
        submit();
      }}
    >
      {subscriptionRequired ? (
        // A gated fan taps the same subscribe flow as the send button.
        <button
          type="button"
          aria-label="Share"
          onClick={() => onSubscribeRequired?.(capability.minLevel)}
          className="bg-surface-3 text-content-2 hover:text-content-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition"
        >
          <Plus className="h-5 w-5" aria-hidden />
        </button>
      ) : (
        <ShareMenu
          onPickPhoto={sharePhoto}
          onPickVideo={shareVideo}
          onShareOrder={openPicker}
          disabled={gateBlocked}
        />
      )}

      <textarea
        ref={textareaRef}
        value={draft}
        onChange={event => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        rows={1}
        disabled={capability.status === 'disabled'}
        placeholder={placeholder}
        aria-label="Message"
        className="bg-surface-1 placeholder:text-content-placeholder focus:ring-brand text-content-1 max-h-32 min-h-[40px] flex-1 resize-none rounded-2xl px-3.5 py-2 text-sm outline-none focus:ring-1 disabled:opacity-60"
      />
      <button
        type="submit"
        disabled={!canSend}
        aria-label="Send"
        className={clsx(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition',
          canSend
            ? 'bg-brand hover:bg-mint-green-200 text-content-on-brand active:scale-95'
            : 'bg-surface-3 text-content-3 cursor-not-allowed',
        )}
      >
        <SendHorizontal className="h-5 w-5" aria-hidden />
      </button>

      {isPickerOpen ? (
        <ShareOrderDialog
          open={isPickerOpen}
          onClose={closePicker}
          role={senderRole}
          currentUserId={currentUser.userId}
          partnerId={targetUser.userId}
          onConfirm={shareOrders}
        />
      ) : null}
    </form>
  );
}
